import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  ArrowsClockwise, 
  MagnifyingGlass, 
  Check,
  X,
  Eye,
  CurrencyDollar
} from '@phosphor-icons/react';
import SortableTable from '../components/SortableTable';
import axios from 'axios';
import { toast } from 'sonner';
import { useSellerMode } from '../contexts/SellerModeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReturnsPage() {
  const { activeSeller } = useSellerMode();
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    return_type: '',
    search: ''
  });
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [qcDialogOpen, setQcDialogOpen] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [qcStatus, setQcStatus] = useState('passed');
  const [qcNotes, setQcNotes] = useState('');
  const [refundAmount, setRefundAmount] = useState('');

  // Re-fetch returns when seller mode changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
      fetchReturns();
    }, 100);
    return () => clearTimeout(timer);
  }, [filters.status, filters.return_type, activeSeller?.user_id]);

  const fetchReturns = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.return_type) params.append('return_type', filters.return_type);
      
      const response = await axios.get(`${API_URL}/api/returns?${params}`, { withCredentials: true });
      setReturns(response.data);
    } catch (error) {
      console.error('Failed to fetch returns:', error);
      toast.error('Failed to fetch returns');
    } finally {
      setLoading(false);
    }
  };

  const handleReturnAction = async (returnId, action, data = {}) => {
    try {
      await axios.post(`${API_URL}/api/returns/${returnId}/${action}`, data, { withCredentials: true });
      toast.success(`Return ${action} successfully`);
      fetchReturns();
      setSelectedReturn(null);
      setQcDialogOpen(false);
      setRefundDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} return`);
    }
  };

  const handleQcSubmit = () => {
    if (selectedReturn) {
      handleReturnAction(selectedReturn.return_id, 'qc', { qc_status: qcStatus, notes: qcNotes });
    }
  };

  const handleRefundSubmit = () => {
    if (selectedReturn && refundAmount) {
      handleReturnAction(selectedReturn.return_id, 'refund', { amount: parseFloat(refundAmount) });
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      accepted: 'primary',
      in_transit: 'primary',
      received: 'primary',
      processed: 'success',
      rejected: 'danger'
    };
    return colors[status] || 'neutral';
  };

  const getQcStatusColor = (status) => {
    if (status === 'passed') return 'success';
    if (status === 'failed') return 'danger';
    return 'neutral';
  };

  const filteredReturns = returns.filter(ret => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      return (
        ret.return_id?.toLowerCase().includes(search) ||
        ret.order_id?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const returnColumns = [
    { key: 'return_id', label: 'Return ID', render: v => <span className="font-mono text-sm font-medium">{v?.slice(-10)}</span> },
    { key: 'order_id', label: 'Order ID', render: v => <span className="font-mono text-sm">{v?.slice(-10)}</span> },
    { key: 'return_type', label: 'Type', render: v => <span className="text-xs font-semibold uppercase tracking-wider px-2 py-1 bg-[#fcfcfc] rounded">{v?.replace('_', ' ')}</span> },
    { key: 'reason', label: 'Reason' },
    { key: 'status', label: 'Status', render: v => <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded sp-badge-${getStatusColor(v)}`}><span className={`status-dot ${getStatusColor(v)}`}></span>{v?.replace('_', ' ')}</span> },
    { key: 'qc_status', label: 'QC', render: v => v ? <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded sp-badge-${getQcStatusColor(v)}`}>{v}</span> : <span className="text-[#5a5c87] text-sm">-</span> },
    { key: 'created_at', label: 'Date', render: v => formatDate(v) },
    { key: 'return_id', label: 'Actions', sortable: false, render: (v, row) => (
      <div className="flex gap-2">
        <Button variant="ghost" size="sm" onClick={() => setSelectedReturn(row)} data-testid={`view-return-${v}`}><Eye size={16} /></Button>
        {row.status === 'pending' && (
          <>
            <Button variant="ghost" size="sm" className="text-[#28cd8c]" onClick={() => handleReturnAction(v, 'accept')} data-testid={`accept-return-${v}`}><Check size={16} /></Button>
            <Button variant="ghost" size="sm" className="text-[#ea4b5a]" onClick={() => handleReturnAction(v, 'reject')} data-testid={`reject-return-${v}`}><X size={16} /></Button>
          </>
        )}
        {row.status === 'received' && !row.qc_status && (
          <Button variant="ghost" size="sm" className="text-[#7d64ff]" onClick={() => { setSelectedReturn(row); setQcDialogOpen(true); }} data-testid={`qc-return-${v}`}>QC</Button>
        )}
        {row.qc_status === 'passed' && !row.refund_amount && (
          <Button variant="ghost" size="sm" className="text-[#28cd8c]" onClick={() => { setSelectedReturn(row); setRefundDialogOpen(true); }} data-testid={`refund-return-${v}`}><CurrencyDollar size={16} /> Refund</Button>
        )}
      </div>
    )}
  ];

  return (
    <DashboardLayout title="Returns" subtitle="Manage return requests and RTO">
      {/* Filters */}
      <div className="bg-white border border-[#e5e5ea] rounded-xl mb-5">
        <div className="action-bar">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9c9db7]" size={16} />
            <Input placeholder="Search by Return ID or Order ID..." value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className="pl-9 h-9 text-sm" data-testid="return-search" />
          </div>
          <Select value={filters.status || "all"} onValueChange={(value) => setFilters({ ...filters, status: value === "all" ? "" : value })}>
            <SelectTrigger className="w-36 h-9" data-testid="return-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="accepted">Accepted</SelectItem>
              <SelectItem value="in_transit">In Transit</SelectItem>
              <SelectItem value="received">Received</SelectItem>
              <SelectItem value="processed">Processed</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.return_type || "all"} onValueChange={(value) => setFilters({ ...filters, return_type: value === "all" ? "" : value })}>
            <SelectTrigger className="w-40 h-9" data-testid="return-type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="delivered_return">Delivered Return</SelectItem>
              <SelectItem value="rto">RTO</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="loading-spinner"><div className="spinner"></div></div>
      ) : (
        <SortableTable columns={returnColumns} data={filteredReturns} pageSize={15} emptyMessage="No returns found" emptyIcon={ArrowsClockwise} />
      )}

      {/* Return Details Dialog */}
      <Dialog open={!!selectedReturn && !qcDialogOpen && !refundDialogOpen} onOpenChange={() => setSelectedReturn(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-bold">
              Return Details
            </DialogTitle>
          </DialogHeader>
          {selectedReturn && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87] mb-1">Return ID</p>
                  <p className="font-mono text-sm">{selectedReturn.return_id}</p>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87] mb-1">Order ID</p>
                  <p className="font-mono text-sm">{selectedReturn.order_id}</p>
                </div>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87] mb-1">Reason</p>
                <p>{selectedReturn.reason}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87] mb-1">Type</p>
                  <p className="capitalize">{selectedReturn.return_type?.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87] mb-1">Status</p>
                  <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded sp-badge-${getStatusColor(selectedReturn.status)}`}>
                    {selectedReturn.status}
                  </span>
                </div>
              </div>
              {selectedReturn.qc_status && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87] mb-1">QC Result</p>
                  <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded sp-badge-${getQcStatusColor(selectedReturn.qc_status)}`}>
                    {selectedReturn.qc_status}
                  </span>
                  {selectedReturn.qc_notes && <p className="text-sm text-[#5a5c87] mt-1">{selectedReturn.qc_notes}</p>}
                </div>
              )}
              {selectedReturn.refund_amount && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87] mb-1">Refund Amount</p>
                  <p className="text-xl font-bold text-[#28cd8c]">₹{selectedReturn.refund_amount}</p>
                </div>
              )}
              {selectedReturn.claim_status && (
                <div className="bg-[#ea4b5a]/10 p-3 border border-[#ea4b5a]">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#ea4b5a] mb-1">Claim Raised</p>
                  <p className="text-sm">Status: {selectedReturn.claim_status}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* QC Dialog */}
      <Dialog open={qcDialogOpen} onOpenChange={() => setQcDialogOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-bold">Quality Check</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87] mb-2">QC Result</p>
              <Select value={qcStatus} onValueChange={setQcStatus}>
                <SelectTrigger className="rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="passed">Passed</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87] mb-2">Notes</p>
              <Input
                value={qcNotes}
                onChange={(e) => setQcNotes(e.target.value)}
                placeholder="Add QC notes..."
                className="rounded-sm"
              />
            </div>
            <Button 
              onClick={handleQcSubmit}
              className="w-full bg-[#7d64ff] hover:bg-[#6153a7] text-white"
            >
              Submit QC
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog open={refundDialogOpen} onOpenChange={() => setRefundDialogOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-bold">Initiate Refund</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87] mb-2">Refund Amount (₹)</p>
              <Input
                type="number"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                placeholder="Enter amount"
                className="rounded-sm"
              />
            </div>
            <Button 
              onClick={handleRefundSubmit}
              disabled={!refundAmount}
              className="w-full bg-[#28cd8c] hover:bg-[#1fb076] text-white"
            >
              Process Refund
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
